import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MemberLogin from 'components/Login/MemberLogin';
import { LoginStep } from 'constants/enums/loginStep.enum';
import Authentication from 'components/Login/Authentication';
import OTPVerification from 'components/Login/OTPVerification';
import SecurityQuestion from 'components/Login/SecurityQuestion';
import MemberVerifyComplete from 'components/Login/MemberVerifyComplete';
import { MemberLoginRes, OTPInfo, Captcha, LogInInfo } from 'bff/models/login';
import { MemberProfile } from 'bff/models/member';
import service from 'bff/services/loginService';
import apiCommonService from 'bff/services/commonService';
import storageService from 'utils/storageService';
import { storageKeys } from 'constants/enums/storageKeys.enum';
import sweetAlertService from 'utils/sweetAlertService';
import { logOut } from 'redux/actions/components/member.action';
import { SactionType } from 'constants/enums/sactionType.enum';
import { fetchMyFavorite } from 'redux/actions/components/myFavorite.action';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
import { RootReducersState } from 'redux/reducers';
import { RouterSettings } from 'constants/models/routerSettings';
import commonService from 'utils/commonService';
import { setLoginModalVisible } from 'redux/actions/modal.action';
import Modal from 'components/Modal';
import { IProps, IState } from './types';
import { DataLayerArgs } from 'react-gtm-module';
import { pushGTM } from 'plugins/gtmService';

const APP_PREFIX = process.env.REACT_APP_PREFIX || '';

/**
 * 驗證一個 URL 是否為安全的同源導向路徑。
 * @param url - 要驗證的 URL 字串。
 * @returns 如果 URL 安全則返回 true，否則返回 false。
 */
const isValidRedirectPath = (url: string | null | undefined): boolean => {
  // 1. 基本檢查：確保 url 是非空字串
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return false;
  }

  try {
    // 2. 使用瀏覽器內建的 URL 解析器來處理
    //    - 相對路徑 (如 /foo/bar) 會被自動轉換為同源的絕對路徑
    //    - `javascript:` 或其他不合法的協定會在此步驟拋出錯誤
    const parsedUrl = new URL(url, window.location.origin);

    // 3. 白名單驗證：只允許 http 和 https 協定
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return false;
    }

    // 4. 白名單驗證：只允許與當前網站同源 (same-origin) 的 URL
    //    這能有效防止「開放重導向 (Open Redirect)」漏洞
    return parsedUrl.origin === window.location.origin;

  } catch (e) {
    // 如果 new URL() 解析失敗，代表格式不正確 (如 javascript:...)，視為不安全
    return false;
  }
};

const LoginModal: React.FC<IProps> = (props) => {
  const routerHistory = useHistory();
  const reduxDispatch = useDispatch();
  const modalState = useSelector((state: RootReducersState) => state.modal.loginModalState);
  const myFavoriteState = useSelector((state: RootReducersState) => state.myFavorite);
  const [state, setState] = useState<IState>({
    captcha: {
      SN: '',
      CAPTCHA_CODE: ''
    },
    memberId: '',
    activeStep: LoginStep.MemberLogin,
    UICode: [],
    originUrl: ''
  });

  useEffect(() => {
    (async () => {
      // 若已完成我的收藏載入，視為已登入，先進行登出
      if (myFavoriteState.fetchFavoriteComplete) {
        reduxDispatch(logOut());
      }

      // let originUrlVal = '';
      // if (routerHistory.location.state) {
      //   const { originUrl }: any = routerHistory.location.state;
      //   originUrlVal = originUrl || '';
      // }

      const initSettingRes = await service.fetchInitSetting();
      setState((prevState) => ({
        ...prevState,
        captcha: { ...initSettingRes.captcha },
        UICode: [...initSettingRes.UICode],
        // originUrl: originUrlVal
      }));
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (myFavoriteState.fetchFavoriteComplete) {
      // if (state.originUrl) {
      //   routerHistory.push(state.originUrl);
      //   return;
      // }

      // 如果路由有狀態，且有記錄前次路由，表為 header 導頁而來，導頁回原本網址
      // if (routerHistory.location.state) {
      //   const { preUrl }: any = routerHistory.location.state;
      //   if (preUrl) {
      //     const lastIndex = preUrl.lastIndexOf('/');
      //     const targetUrl = preUrl.substring(lastIndex);
      //     routerHistory.push(`${process.env.REACT_APP_PREFIX}${targetUrl}`);
      //     return;
      //   }
      // }
      // routerHistory.push(RouterSettings.Home);
      props.onLoginDone();
    }
    // commonService.scrollToTop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myFavoriteState.fetchFavoriteComplete, routerHistory, state.originUrl]);

  useEffect(() => {
    return () => {
      handleSetActivePage(LoginStep.MemberLogin);
    };
  }, []);

  /**
   * @description 切換登入流程頁面
   */
  const handleSetActivePage = (step: LoginStep) => {
    setState((prevState) => ({
      ...prevState,
      activeStep: step
    }));
  };

  /**
   * @description 會員登入 Page 1 取得驗證碼方法
   */
  const handleFetchCaptcha = async () => {
    const captcha = await service.fetchCaptcha();
    setState((prevState) => ({
      ...prevState,
      captcha: { ...captcha as Captcha }
    }));
  };

  /**
   * @description 會員登入 Page 1 進行登入檢核方法
   * @param memberId 會員登入帳號
   * @param memberLoginRes 登入檢核結果-會員姓名、手機、 Email 資訊
   */
  const handleMemberLogin = async (memberId: string, memberLoginRes: MemberLoginRes) => {
    setState((prevState) => ({
      ...prevState,
      activeStep: LoginStep.Authentication,
      memberId: memberId,
      memberLoginInfo: { ...memberLoginRes }
    }));
  };

  /**
   * @description 從選取驗證方式頁返回登入頁，重新刷新驗證碼
   */
  const handleGoBackMemberLogin = async () => {
    const captcha = await service.fetchCaptcha();
    setState((prevState) => ({
      ...prevState,
      activeStep: LoginStep.MemberLogin,
      captcha: { ...captcha as Captcha }
    }));
  };

  /**
   * @description 會員登入 Page 2 取得 OTP 驗證碼方法
   * @param memberProfile 會員資料
   * @param updateOTP 是否重新更新 OTP
   */
  const handleFetchOTP = async (memberProfile: MemberProfile, updateOTP?: boolean) => {
    if (!updateOTP) return;

    const result = await apiCommonService.fetchOTP(memberProfile, SactionType.Login);
    if (result) {
      setState((prevState) => ({
        ...prevState,
        activeStep: LoginStep.OTPVerification,
        memberVerifyInfo: { ...memberProfile },
        otpInfo: { ...result.TRANRS },
        getOTPTime: new Date().getTime()
      }));
    }
  };

  /**
   * @description 會員登入 Page 2 重新取得 OTP 驗證碼方法
   */
  const handleResentOTP = async () => {
    const result = await apiCommonService.fetchOTP(state.memberVerifyInfo as MemberProfile, SactionType.Login, true);
    if (result) {
      setState((prevState) => ({
        ...prevState,
        otpInfo: { ...result.TRANRS },
        getOTPTime: new Date().getTime()
      }));
    }
  };

  /**
   * @description 會員登入 Page 3 驗證 OTP認證碼
   * @param otpVerificationReq OTP 驗證頁 Request Model
   */
  const handleVerifyOTP = async (otpCode: string) => {
    const result = await apiCommonService.verifyOTP(state.otpInfo as OTPInfo, otpCode, SactionType.Login, true);
    if (!result) return;

    storageService.removeItem(storageKeys.MemberId);
    storageService.removeItem(storageKeys.LoginInfo);
    commonService.storageData(storageKeys.MemberId, state.memberId);
    commonService.storageData(storageKeys.LoginInfo, result);

    let addFavoriteItem;
    if (myFavoriteState.unloginAddItem.InsTypeId && myFavoriteState.unloginAddItem.PlanId && myFavoriteState.unloginAddItem.ProdId) {
      addFavoriteItem = commonService.getAddFavoriteItem(myFavoriteState.unloginAddItem.InsTypeId, myFavoriteState.unloginAddItem.ProdId, myFavoriteState.unloginAddItem.PlanId);
    }

    reduxDispatch(fetchMyFavorite(state.memberId, addFavoriteItem));

    setState((prevState) => ({
      ...prevState,
      getOTPTime: 0
    }));

    pushGTM(getGtmArg('login'));
  };

  /**
   * @description 會員登入 驗證安全問題查核
   * @param validSecqueReq 會員驗證問題查核 Request Model
   */
  const handleFetchSecque = async () => {
    const secque = await service.fetchMemberSecque(state.memberId);

    if (secque && secque.SECQUE.SECQUE_ID && secque.SECQUE.SECQUE_QUESTION) {
      setState((prevState) => ({
        ...prevState,
        activeStep: LoginStep.SecurityQuestion,
        secque: secque
      }));
    } else {
      sweetAlertService.modal({
        icon: SweetAlertIcon.Info,
        title: '無法取得安全問題',
        html: '於上班日周一至週五08:30-17:30來電洽0800-024-024取得其他登入方式，謝謝您。'
      });
    }
  };

  /**
   * @description 會員安全問題查核通過，導頁至會員維護頁
   */
  const handleMemberVerifyComplete = () => {
    const path = {
      pathname: RouterSettings.MemberCenter.MemberMaintain,
      state: {
        preUrl: RouterSettings.Login,
        // Only include originUrl in the state if it's a valid, safe path
        originUrl: isValidRedirectPath(state.originUrl) ? state.originUrl : undefined
      }
    };
    routerHistory.push(path);
  };

  const handleModalCancel = () => {
    props.onClose();
    handleSetActivePage(LoginStep.MemberLogin);
    reduxDispatch(setLoginModalVisible(false));
  };

  const renderActivePageElement = () => {
    let activePageElement;
    switch (state.activeStep) {
      case LoginStep.Authentication: // 身份驗證頁
        activePageElement = (
          <Authentication
            memberId={state.memberId}
            memberInfo={state.memberLoginInfo as MemberLoginRes}
            onSubmit={handleFetchOTP}
            onGoBack={() => handleGoBackMemberLogin()}
          />
        );
        break;
      case LoginStep.OTPVerification: // OTP驗證頁
        activePageElement = (
          <OTPVerification
            memberId={state.memberId}
            otpInfo={state.otpInfo as OTPInfo}
            getOTPTime={state.getOTPTime as number}
            UICode={state.UICode}
            onResentOTP={() => handleResentOTP()}
            onVerifyOTP={handleVerifyOTP}
            onFetchSecque={handleFetchSecque}
            onGoBack={() => handleSetActivePage(LoginStep.Authentication)}
          />
        );
        break;
      case LoginStep.SecurityQuestion: // 詢問安全問題頁
        activePageElement = (
          <SecurityQuestion
            secque={state.secque}
            memberId={state.memberId}
            UICode={state.UICode}
            onVerifySuccess={() => handleSetActivePage(LoginStep.MemberVerifyComplete)}
            onGoBack={() => handleSetActivePage(LoginStep.OTPVerification)}
          />
        );
        break;
      case LoginStep.MemberVerifyComplete: // 修改會員資料提示頁
        commonService.storageData(storageKeys.MemberId, state.memberId);
        activePageElement = (
          <MemberVerifyComplete
            onSubmit={handleMemberVerifyComplete}
          />
        );
        break;
      case LoginStep.MemberLogin: // 會員登入頁
      default:
        activePageElement = (
          <MemberLogin
            title={props.title}
            captcha={state.captcha}
            UICode={state.UICode}
            originUrl={state.originUrl}
            onSubmit={handleMemberLogin}
            onCaptchaChange={handleFetchCaptcha}
          />
        );
        break;
    }
    return activePageElement;
  };

  const getGtmArg = (event:string): DataLayerArgs => {
    // 取得userID
    const loginInfo = JSON.parse(storageService.getItem(storageKeys.LoginInfo)) as LogInInfo;
    return {
      dataLayer: {
        event: 'td_login',
        UserID: loginInfo.sub
      }
    };
  };

  return (
    <div className="login-modal">
      <Modal
        visible={modalState.visible}
        confirmButton={false}
        cancelButton={false}
        overlayDismiss={false}
        onCancel={handleModalCancel}
      >
        {renderActivePageElement()}
      </Modal>
    </div>
  );
};

export default LoginModal;
